(() => {
  if (window.__oshaGameGalleryRuntime) return;
  window.__oshaGameGalleryRuntime = true;

  const AUTOPLAY_MS = 4000;
  const INTERACTION_PAUSE_MS = 7000;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const galleryStates = new WeakMap();
  const galleries = new Set();

  const scriptEl = document.currentScript || [...document.scripts].find((script) => script.src.includes('osha-game-gallery.js'));
  if (scriptEl?.src && !document.querySelector('link[data-osha-game-gallery-css]')) {
    try {
      const cssUrl = new URL(scriptEl.src, window.location.href);
      cssUrl.pathname = cssUrl.pathname.replace('osha-game-gallery.js', 'osha-game-gallery.css');
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl.toString();
      link.dataset.oshaGameGalleryCss = '';
      document.head.appendChild(link);
    } catch (_) {}
  }

  let productDataPromise;
  const getProductData = () => {
    if (productDataPromise) return productDataPromise;
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    productDataPromise = fetch(url.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Product JSON unavailable');
        return response.json();
      })
      .catch(() => null);
    return productDataPromise;
  };

  const normalizeImage = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const imageKey = (src) => {
    try {
      const url = new URL(src, window.location.origin);
      return url.pathname.replace(/_[0-9]+x[0-9]+(?=\.[a-z]+$)/i, '');
    } catch (_) {
      return src || '';
    }
  };

  const make = (tag, className) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    return el;
  };

  const getState = (gallery) => {
    let state = galleryStates.get(gallery);
    if (!state) {
      state = { timer: 0, resumeTimer: 0, hovering: false, changeToken: 0 };
      galleryStates.set(gallery, state);
    }
    return state;
  };

  const clearAutoplay = (gallery) => {
    const state = getState(gallery);
    window.clearTimeout(state.timer);
    state.timer = 0;
  };

  const centerActiveThumb = (gallery, button) => {
    const rail = gallery.querySelector('.osha-game-gallery__thumbs');
    if (!rail || !button) return;
    const target = button.offsetLeft - (rail.clientWidth - button.offsetWidth) / 2;
    try {
      rail.scrollTo({
        left: Math.max(0, target),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    } catch (_) {
      rail.scrollLeft = Math.max(0, target);
    }
  };

  const switchToThumb = (gallery, button, options = {}) => {
    if (!gallery || !button) return;
    const main = gallery.querySelector('[data-osha-gallery-main]');
    const nextSrc = button.dataset.image;
    if (!main || !nextSrc) return;

    const state = getState(gallery);
    const token = ++state.changeToken;

    gallery.querySelectorAll('[data-osha-gallery-thumb]').forEach((thumb) => {
      const active = thumb === button;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (options.scrollThumb !== false) centerActiveThumb(gallery, button);
    if (imageKey(main.src) === imageKey(nextSrc)) return;

    main.classList.add('is-switching');
    const preloader = new Image();
    preloader.onload = () => {
      if (state.changeToken !== token || !gallery.isConnected) return;
      main.src = nextSrc;
      main.alt = button.dataset.alt || '';
      requestAnimationFrame(() => main.classList.remove('is-switching'));
    };
    preloader.onerror = () => {
      if (state.changeToken === token) main.classList.remove('is-switching');
    };
    preloader.src = nextSrc;
  };

  const scheduleAutoplay = (gallery, delay = AUTOPLAY_MS) => {
    if (!gallery?.isConnected) return;
    const state = getState(gallery);
    clearAutoplay(gallery);

    const thumbs = [...gallery.querySelectorAll('[data-osha-gallery-thumb]')];
    if (reduceMotion.matches || document.hidden || state.hovering || thumbs.length < 2) return;

    state.timer = window.setTimeout(() => {
      if (!gallery.isConnected) {
        galleries.delete(gallery);
        return;
      }
      const currentIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      const next = thumbs[(currentIndex + 1) % thumbs.length];
      switchToThumb(gallery, next);
      scheduleAutoplay(gallery, AUTOPLAY_MS);
    }, delay);
  };

  const pauseTemporarily = (gallery, delay = INTERACTION_PAUSE_MS) => {
    if (!gallery) return;
    const state = getState(gallery);
    clearAutoplay(gallery);
    window.clearTimeout(state.resumeTimer);
    state.resumeTimer = window.setTimeout(() => scheduleAutoplay(gallery), delay);
  };

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.oshaAutoplayReady === 'true') return;
    gallery.dataset.oshaAutoplayReady = 'true';
    galleries.add(gallery);
    getState(gallery);

    gallery.addEventListener('mouseenter', () => {
      const state = getState(gallery);
      state.hovering = true;
      clearAutoplay(gallery);
    });

    gallery.addEventListener('mouseleave', () => {
      const state = getState(gallery);
      state.hovering = false;
      scheduleAutoplay(gallery, 900);
    });

    gallery.addEventListener('pointerdown', () => pauseTemporarily(gallery), { passive: true });
    gallery.addEventListener('focusin', () => pauseTemporarily(gallery));
    gallery.addEventListener('keydown', () => pauseTemporarily(gallery));

    scheduleAutoplay(gallery);
  };

  const initAllGalleries = () => {
    document.querySelectorAll('[data-osha-game-gallery]').forEach(initGallery);
    galleries.forEach((gallery) => {
      if (!gallery.isConnected) galleries.delete(gallery);
    });
  };

  const buildGallery = (product, screenshots) => {
    const gallery = make('div', 'osha-game-gallery');
    gallery.dataset.oshaGameGallery = '';
    gallery.dataset.oshaGeneratedGallery = '';

    const heading = make('div', 'osha-game-gallery__heading');
    const headingLeft = make('div');
    const title = make('span');
    title.textContent = 'GAMEPLAY GALLERY';
    const divider = make('i');
    const ar = make('span');
    ar.dir = 'rtl';
    ar.textContent = 'لقطات من اللعبة';
    headingLeft.append(title, divider, ar);
    const count = make('small');
    count.textContent = `${screenshots.length} ${screenshots.length === 1 ? 'SHOT' : 'SHOTS'}`;
    heading.append(headingLeft, count);

    const viewer = make('div', 'osha-game-gallery__viewer');
    const main = document.createElement('img');
    main.src = screenshots[0];
    main.alt = `${product.title || 'Game'} screenshot`;
    main.loading = 'lazy';
    main.dataset.oshaGalleryMain = '';
    viewer.appendChild(main);

    const thumbs = make('div', 'osha-game-gallery__thumbs');
    thumbs.setAttribute('role', 'group');
    thumbs.setAttribute('aria-label', 'Game screenshots');

    screenshots.forEach((src, index) => {
      const button = make('button', `osha-game-gallery__thumb${index === 0 ? ' is-active' : ''}`);
      button.type = 'button';
      button.dataset.oshaGalleryThumb = '';
      button.dataset.image = src;
      button.dataset.alt = `${product.title || 'Game'} screenshot ${index + 1}`;
      button.setAttribute('aria-label', `Show screenshot ${index + 1}`);
      button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      button.appendChild(img);
      thumbs.appendChild(button);
    });

    gallery.append(heading, viewer, thumbs);
    return gallery;
  };

  let ensuring = false;
  const ensureGallery = async () => {
    if (ensuring || !window.location.pathname.includes('/products/')) return;
    ensuring = true;
    try {
      const product = await getProductData();
      if (!product || product.type !== 'Digital Game') return;

      const mediaColumn = document.querySelector('.product-information__media');
      if (!mediaColumn) return;

      const existing = mediaColumn.querySelector('[data-osha-game-gallery]');
      if (existing) {
        initGallery(existing);
        return;
      }

      const allImages = (product.images || []).map(normalizeImage).filter(Boolean);
      const featured = normalizeImage(product.featured_image) || allImages[0] || '';
      const featuredKey = imageKey(featured);
      const screenshots = allImages
        .filter((src, index) => index > 0 || imageKey(src) !== featuredKey)
        .filter((src) => imageKey(src) !== featuredKey);

      if (!screenshots.length) return;
      const gallery = buildGallery(product, screenshots);
      mediaColumn.appendChild(gallery);
      initGallery(gallery);
    } finally {
      ensuring = false;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-osha-gallery-thumb]');
    if (!button) return;
    const gallery = button.closest('[data-osha-game-gallery]');
    if (!gallery) return;
    initGallery(gallery);
    switchToThumb(gallery, button);
    pauseTemporarily(gallery);
  });

  document.addEventListener('visibilitychange', () => {
    galleries.forEach((gallery) => {
      if (!gallery.isConnected) {
        galleries.delete(gallery);
        return;
      }
      if (document.hidden) clearAutoplay(gallery);
      else scheduleAutoplay(gallery, 1200);
    });
  });

  const onMotionChange = () => {
    galleries.forEach((gallery) => {
      if (reduceMotion.matches) clearAutoplay(gallery);
      else scheduleAutoplay(gallery, 1200);
    });
  };
  if (typeof reduceMotion.addEventListener === 'function') reduceMotion.addEventListener('change', onMotionChange);
  else if (typeof reduceMotion.addListener === 'function') reduceMotion.addListener(onMotionChange);

  const observer = new MutationObserver(() => {
    window.clearTimeout(window.__oshaGalleryEnsureTimer);
    window.__oshaGalleryEnsureTimer = window.setTimeout(() => {
      initAllGalleries();
      ensureGallery();
    }, 40);
  });

  const start = () => {
    initAllGalleries();
    ensureGallery();
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => {
      initAllGalleries();
      ensureGallery();
    }, 250);
    window.setTimeout(() => {
      initAllGalleries();
      ensureGallery();
    }, 900);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();